import React, { useEffect, useState } from "react";
import { 
  Tab, 
  Tabs, 
  Avatar, 
  Button, 
  Input, 
  Textarea, 
  Switch,
  Card,
  CardBody,
  useDisclosure,
  Chip,
  Divider,
  Spinner
} from "@nextui-org/react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faBuilding, faKey, faTrash, faBell, faCog, faShield, faChartLine, faBrain, faSearch, faQuestionCircle, faChartBar } from "@fortawesome/free-solid-svg-icons";

import ApiKey from "./ApiKey/ApiKey";
import Organization from "./Organization";
import BreadCrumbsPage from "../../components/BreadCrumbsPage";
import DangerZoneOrg from "./DangerZone/DangerZoneOrg";
import ModalPopup from "../../components/ModalPopup";
import DeleteModal from "../../components/DeleteModal";
import { 
  getOnedata, 
  UpdateOneData, 
  DeleteOnedata,
  getAlldata, 
  UpdateSingleCollectionData,
} from "../../controllers/APIController";
import LoadingPage from "../../components/Loader/LoadingPage";



import AiSettings from "./AiSettings/AiSettings";
import Profile from "./Profile/Profile";
import { useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * Settings Component
 * Main settings page with multiple tabs for user profile, organization, and system settings
 * Handles user profile updates, organization management, and account deletion
 * @returns {JSX.Element} Settings page with tabbed interface
 */
const Settings = () => {
  // Log page access for audit
  // usePageAudit("Settings");
  
  // Component state
  const [activeTab, setActiveTab] = useState("profile");
  const [userDetails, setUserDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const queryClient = useQueryClient();

  // Form data state
  const [formData, setFormData] = useState({
    FirstName: "",
    LastName: "",
    ProfilePicture: "",
  });
  
  const [orgFormData, setOrgFormData] = useState({
    Name: "",
    Address: "",
    City: "",
    Postal: "",
  });
  
  const [orgFormDataError, setOrgFormDataError] = useState({
    NameError: "",
    AddressError: "",
    CityError: "",
    PostalError: "",
  });
  
  const [validationConditions, setValidationConditions] = useState(false);
  
  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState({
    email: true,
    slack: false,
    teams: false,
    discord: false
  });



  // Modal disclosure hooks
  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onOpenChange: onModalOpenChange,
  } = useDisclosure();
  
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onOpenChange: onDeleteOpenChange,
  } = useDisclosure();

  // Navigation and routing
  const navigate = useNavigate();
  const { _orgId } = useParams();
  
  // User data from session storage
  const userLogged = JSON.parse(sessionStorage.getItem("userData"));
  const userId = userLogged && userLogged.user.id;

  // Get current organization ID
  const orgInfo = userLogged && userLogged.user.LastOrgId;

  // Avatar color configuration
  const avatarColors = ["secondary", "success", "warning", "danger", "primary"];
  const numColors = avatarColors.length;
  const colorIndex = userDetails && userDetails.id % numColors;

  /**
   * Load user and organization data on component mount
   * Fetches user profile and organization details from API
   */
    const { data: userData } = useQuery({
      queryKey: ["users", userId],
      queryFn: () => getOnedata("users", userId),
    });

    const { data: orgData } = useQuery({
      queryKey: ["tenant"],
      queryFn: () => getAlldata("tenant"),
    });

  useEffect(() => {
    // Load user details
    // const userData = await getOnedata("users", userId);
    setUserDetails(userData);
    setFormData({
      FirstName: userData?.FirstName || "",
      LastName: userData?.LastName || "",
      ProfilePicture: userData?.ProfilePictureUrl || "",
    });

    // Load organization details
    // const orgData = await getAlldata("tenant");
    // console.log("Organization data received:", orgData);

    if (orgData && Array.isArray(orgData) && orgData.length > 0) {
      // Handle array response from getAlldata
      const firstOrg = orgData[0];
      if (firstOrg && firstOrg.attributes) {
        setOrgFormData(firstOrg.attributes);
      }
    } else if (orgData && orgData.data && orgData.data.attributes) {
      // Handle object response with nested data
      setOrgFormData(orgData.data.attributes);
    } else if (orgData && orgData.attributes) {
      // Handle direct object response
      setOrgFormData(orgData.attributes);
    } else {
      console.warn("No valid organization data found:", orgData);
    }
  }, [orgData, userData]);



  /**
   * Validate organization form data
   * Checks required fields and sets error messages
   * @returns {boolean} Whether the form is valid
   */
  const validateOrgForm = () => {
    let isValid = true;
    const errors = {};

    if (!orgFormData.Name.trim()) {
      errors.NameError = "Organization name is required";
      isValid = false;
    }

    if (!orgFormData.Address.trim()) {
      errors.AddressError = "Address is required";
      isValid = false;
    }

    if (!orgFormData.City.trim()) {
      errors.CityError = "City is required";
      isValid = false;
    }

    if (!orgFormData.Postal.trim()) {
      errors.PostalError = "Postal code is required";
      isValid = false;
    }

    setOrgFormDataError(errors);
    return isValid;
  };

  /**
   * Save organization changes to API
   * Validates form and updates organization data
   */
  const saveOrgChanges = async () => {
    if (!validateOrgForm()) {
      setValidationConditions(true);
      return;
    }

    try {
      await UpdateSingleCollectionData("tenant", orgFormData);
       queryClient.invalidateQueries({ queryKey: ["tenant"] });
      setValidationConditions(false);
    } catch (error) {
      console.error("Error updating organization:", error);
    }
  };

  /**
   * Update user profile information
   * Validates form and sends update to API
   * @param {Function} onClose - Function to close modal after update
   */
  const updateProfile = async (onClose) => {
    if (!validateForm()) {
      setValidationConditions(true);
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        FirstName: formData.FirstName,
        LastName: formData.LastName,
        ProfilePictureUrl: formData.ProfilePicture,
      };

      await UpdateOneData("users", userId, payload);
       queryClient.invalidateQueries({ queryKey: ["users"] });
      
      // Log profile update for audit
      // try {
      //   await logAuditEvent('PROFILE_UPDATED', `User: ${userId}`, {
      //     userId: userId,
      //     organizationId: _orgId,
      //     updatedFields: Object.keys(payload)
      //   });
      // } catch (error) {
      //   console.error("Error logging profile update:", error);
      // }
      
      onClose();
    } catch (error) {
      console.error("Error updating profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Delete user account
   * Removes user data and redirects to sign-in page
   */
  const deleteAccount = async () => {
    setIsLoading(true);
    try {
      await DeleteOnedata("users", userId);
       queryClient.invalidateQueries({ queryKey: ["users"] });
      // Log account deletion for audit
      // try {
      //   await logAuditEvent('ACCOUNT_DELETED', `User: ${userId}`, {
      //     userId: userId,
      //     organizationId: _orgId,
      //     action: 'ACCOUNT_DELETION'
      //   });
      // } catch (error) {
      //   console.error("Error logging account deletion:", error);
      // }
      
      navigate("/sign-in");
      sessionStorage.clear();
    } catch (error) {
      console.error("Error deleting account:", error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Validate profile form data
   * Checks required fields for profile update
   * @returns {boolean} Whether the form is valid
   */
  const validateForm = () => {
    return formData.FirstName.trim() !== "" && formData.LastName.trim() !== "";
  };

  /**
   * Handle notification setting changes
   * Updates notification preferences state
   * @param {string} key - Setting key to update
   * @param {boolean} value - New value for the setting
   */
  const handleNotificationChange = (key, value) => {
    setNotificationSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  /**
   * Modal body content for profile editing
   * Contains form fields for profile updates
   * @returns {JSX.Element} Profile edit form modal content
   */
  const ModalBodyData = () => (
    <>
      <div className="text-lg font-semibold text-slate-900 mb-4">Edit Profile</div>
      <div className="space-y-4">
        {/* Profile Picture Section */}
        <div className="flex items-center gap-4">
          <Avatar
            radius="full"
            size="lg"
            src={formData.ProfilePicture}
            color={avatarColors[colorIndex]}
            name={
              userDetails && userDetails.FirstName !== null
                ? userDetails.FirstName.slice(0, 1).toUpperCase()
                : userDetails && userDetails.email.slice(0, 1).toUpperCase()
            }
          />
          <Button size="sm" variant="bordered">
            Change photo
          </Button>
        </div>
        
        {/* First Name Input */}
        <div>
          <label className="text-xs font-medium text-slate-700 mb-1 block">
            First Name
          </label>
          <Input
            type="text"
            size="sm"
            value={formData.FirstName}
            onChange={(e) => setFormData({ ...formData, FirstName: e.target.value })}
            placeholder="Enter your first name"
            isInvalid={validationConditions && !formData.FirstName.trim()}
            errorMessage={validationConditions && !formData.FirstName.trim() ? "First name is required" : ""}
          />
        </div>
        
        {/* Last Name Input */}
        <div>
          <label className="text-xs font-medium text-slate-700 mb-1 block">
            Last Name
          </label>
          <Input
            type="text"
            size="sm"
            value={formData.LastName}
            onChange={(e) => setFormData({ ...formData, LastName: e.target.value })}
            placeholder="Enter your last name"
            isInvalid={validationConditions && !formData.LastName.trim()}
            errorMessage={validationConditions && !formData.LastName.trim() ? "Last name is required" : ""}
          />
        </div>
        
        {/* Profile Picture URL Input */}
        <div>
          <label className="text-xs font-medium text-slate-700 mb-1 block">
            Profile Picture URL
          </label>
          <Input
            type="url"
            size="sm"
            value={formData.ProfilePicture}
            onChange={(e) => setFormData({ ...formData, ProfilePicture: e.target.value })}
            placeholder="Enter profile picture URL"
          />
        </div>
      </div>
    </>
  );

  const footerCreate = (onClose) => (
    <Button 
      color="primary" 
      className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 font-medium"
      onClick={() => updateProfile(onClose)}
      isLoading={isLoading}
    >
      Update Profile
    </Button>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-4 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-slate-900">Settings</h1>
              <p className="text-slate-600 mt-1 text-sm">Manage your account and organization configuration</p>
            </div>
            <div className="flex items-center gap-2">
              <Chip color="success" variant="flat" size="sm">
                <FontAwesomeIcon icon={faShield} className="mr-1 text-xs" />
                Secure
              </Chip>
            </div>
          </div>
        </div>
      </div>

      <BreadCrumbsPage>
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="max-w-7xl mx-auto">
            <LoadingPage isLoading={isLoading} loadingText="Loading settings...">
              {!isLoading && (
                <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-200">
                    <h3 className="text-base font-semibold text-slate-900">Configuration</h3>
                    <p className="text-xs text-slate-600 mt-1">Manage your account and organization settings</p>
                  </div>
                  <div className="p-4">
                  <Tabs 
                    selectedKey={activeTab}
                    onSelectionChange={setActiveTab}
                    variant="light"
                    classNames={{
                      tabList: "gap-6 w-full relative rounded-none p-0 border-b border-divider",
                      cursor: "w-full bg-slate-100",
                      tab: "max-w-fit px-2 h-12",
                      tabContent: "group-data-[selected=true]:text-emerald-600",
                      panel: "min-h-[600px]"
                    }}
                  >
                    <Tab 
                      key="profile" 
                      title={
                        <div className="flex items-center gap-2">
                          <FontAwesomeIcon icon={faUser} />
                          <span>Profile</span>
                        </div>
                      }
                    >
                      <div className="mt-6 pb-8">
                        <Profile />
                      </div>


                    </Tab>

                    <Tab 
                      key="organization" 
                      title={
                        <div className="flex items-center gap-2">
                          <FontAwesomeIcon icon={faBuilding} />
                          <span>Organization</span>
                        </div>
                      }
                    >
                      <div className="mt-6 pb-8">
                        <Organization />
                      </div>
                    </Tab>



                    <Tab 
                      key="api-key" 
                      title={
                        <div className="flex items-center gap-2">
                          <FontAwesomeIcon icon={faKey} />
                          <span>API Key</span>
                        </div>
                      }
                    >
                      <div className="mt-6 pb-8">
                        <ApiKey />
                      </div>
                    </Tab>

                    <Tab 
                      key="ai-settings" 
                      title={
                        <div className="flex items-center gap-2">
                          <FontAwesomeIcon icon={faBrain} />
                          <span>AI Settings</span>
                        </div>
                      }
                    >
                      <div className="mt-6 pb-8">
                        <AiSettings />
                      </div>
                    </Tab>

                    <Tab 
                      key="danger-zone" 
                      title={
                        <div className="flex items-center gap-2">
                          <FontAwesomeIcon icon={faTrash} />
                          <span>Danger Zone</span>
                        </div>
                      }
                    >
                      <div className="mt-6 pb-8">
                        <DangerZoneOrg />
                      </div>
                    </Tab>
                  </Tabs>
                </div>
                </div>
              )}
            </LoadingPage>
          </div>
        </div>
      </BreadCrumbsPage>

      {/* Modals */}
      <ModalPopup
        isOpen={isModalOpen}
        onOpenChange={onModalOpenChange}
        ModalBodyData={ModalBodyData}
        footer={footerCreate}
      />

      <DeleteModal
        isOpen={isDeleteOpen}
        onOpenChange={onDeleteOpenChange}
        deleteData={""}
        setDeleteData={""}
        Collectionname={"user"}
        setLoader={setIsLoading}
        DeleteHandler={deleteAccount}
      />
    </div>
  );
};

export default Settings;
